// Build-in hooks
import { useEffect, useState } from "react";

// Import necessary icons
import { IoMdClose } from "react-icons/io";

// Import custom components
import VersionTrackRow from "../Track/VersionTrackRow";

const PlayerVersions = ({ isOpen, onClose, track = {}, editIndex }) => {
	const versions = track.editInfos?.[editIndex]?.versions;
	const isEmpty = versions?.length === 0; // Check if the versions is empty
	const [shouldRender, setShouldRender] = useState(isOpen); // State to control rendering of the component
	const [fadeClass, setFadeClass] = useState("animate-top-to-bottom-opacity"); // State to manage animation classes
	const [leftPosition, setLeftPosition] = useState("2px");
	const [contentHeight, setContentHeight] = useState("253px");
	const [containerHeight, setContainerHeight] = useState("auto");

	useEffect(() => {
		const calculateDimensions = () => {
			const width = window.innerWidth;
			const height = window.innerHeight;

			// Calculate left position
			if (width < 640) {
				setLeftPosition("max(2px, calc(100vw - 483px))");
			} else if (width < 768) {
				setLeftPosition("max(14px, calc(100vw - 543px))");
			} else if (width < 1024) {
				setLeftPosition("max(16px, calc(100vw - 583px))");
			} else if (width < 1280) {
				setLeftPosition("max(16px, calc(50vw - 453px))");
			} else {
				setLeftPosition("max(16px, calc(50vw - 483px))");
			}

			// Calculate content height based on track row heights and screen size
			// Track row heights: 45px (sm), 50px (md), 60px (lg)
			// Header heights: ~40px (sm), ~45px (md), ~55px (lg)
			// Bottom player heights: 150px (sm), 180px (md), 106px (lg)
			
			let trackRowHeight, headerHeight, bottomPlayerHeight, maxTracks;
			
			if (width >= 1024) {
				// Large screens - show max 5 tracks
				trackRowHeight = 70;
				headerHeight = 55;
				bottomPlayerHeight = 113;
				maxTracks = 5;
			} else if( width>=768){
				trackRowHeight	= 50;
				headerHeight=53;
				bottomPlayerHeight=207;
				maxTracks = Math.min(5, Math.floor((height - bottomPlayerHeight - headerHeight) / trackRowHeight));
			} else if (width >= 640) {
				// Medium screens - show max 4 tracks, but allow scrolling
				trackRowHeight = 50;
				headerHeight = 52;
				bottomPlayerHeight = 215;
				maxTracks = Math.min(5, Math.floor((height - bottomPlayerHeight - headerHeight) / trackRowHeight));
			} else {
				// Small screens - calculate based on available space
				trackRowHeight = 45;
				headerHeight = 40;
				bottomPlayerHeight = 160;
				maxTracks = Math.min(5, Math.floor((height - bottomPlayerHeight - headerHeight) / trackRowHeight));
			}

			// Ensure we show at least 2 tracks and no more than 5 on large screens
			maxTracks = Math.max(2, Math.min(maxTracks, width >= 1024 ? 5 : 6));
			
			// Calculate the actual content height
			const calculatedHeight = maxTracks * trackRowHeight + headerHeight;

			// Add some padding and ensure minimum height
			const finalHeight = Math.max(calculatedHeight, 100);
			
			setContentHeight(`${finalHeight}px`);
			setContainerHeight(`${finalHeight - headerHeight}px`);
		};

		calculateDimensions();
		window.addEventListener("resize", calculateDimensions);
		return () => window.removeEventListener("resize", calculateDimensions);
	}, []);

	useEffect(() => {
		// Handle fade-in and fade-out animations based on `isOpen` prop
		if (isOpen) {
			setShouldRender(true);
			setFadeClass("animate-bottom-to-top-opacity");
		} else {
			setFadeClass("duration-300 animate-top-to-bottom-opacity");
			const timeout = setTimeout(() => {
				setShouldRender(false); // Delay unmounting to allow animation to complete
			}, 300); // Match animation duration
			return () => clearTimeout(timeout);
		}
	}, [isOpen]);

	if (isEmpty) return null; // Prevent rendering if there are no versions

	if (!shouldRender) return null; // Prevent rendering if `shouldRender` is false

	return (
		<aside
			className={`fixed left-0 bottom-[160px] sm:bottom-[180px] md:bottom-[207px] lg:bottom-[113px] w-[380px] max-w-full z-[3999] bg-transparent text-white shadow-2xl rounded-l-lg ${fadeClass}`}
			style={{
				backdropFilter: "blur(10px)",
				left: 0,
				height: `calc(${contentHeight})`, // content height + header + padding
				maxHeight: "90vh",
			}}
		>
			{/* Background overlay */}
			<div className="z-50 absolute bg-[#0E0E11]/80 w-full h-full rounded-l-lg" />

			{/* Header section */}
			<div className="z-[60] flex relative items-center justify-between px-3 sm:px-4 lg:px-6 py-2 sm:py-3 lg:py-5 border-b border-[#1F1F1F] overflow-y-auto">
				<h2 className="text-white text-xs sm:text-sm lg:text-base font-bold uppercase tracking-wider pt-2">
					Available Versions
				</h2>
				<button
					onClick={onClose}
					className="text-gray-400 hover:text-white transition"
				>
					<IoMdClose className="w-4 h-4 sm:w-5 sm:h-5 lg:w-6 lg:h-6" />
				</button>
			</div>

			{/* Content section */}
			<div
				className="z-[60] relative flex flex-1 items-start justify-center text-center px-3 sm:px-4 lg:px-6 pt-2 sm:pt-3 lg:pt-4 overflow-y-auto bg-[#0E0E11]/80"
				style={{
					height: containerHeight,
					maxHeight: contentHeight,
				}}
			>
				{!isEmpty && (
					// Render the list of tracks in the versions
					<div className="w-full h-full ">
						<ul className="flex flex-col items-start w-full">
							{versions?.map((version, i) => (
								<VersionTrackRow
									key={track.id || i} // Use track ID or index as key
									trackMetadata={track} // Pass track metadata
									remixMetadata={track.editInfos?.[editIndex]}
									version={version}
									playIndex={i} // Pass index of the track
								/>
							))}
						</ul>
					</div>
				)}
			</div>
		</aside>
	);
};

export default PlayerVersions;